import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  ListItemIcon,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  AccessTime,
  Quiz as QuizIcon,
  Replay,
  PlayArrow,
  Search,
  BarChart as BarChartIcon,
  PauseCircle,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../../api';
import useExamMode from '../../../hooks/useExamMode';

const TAB_EXAM = 0;
const TAB_RESULT = 1;

const StudentMockExams = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isTestingMode } = useExamMode();
  const isTestMode = isTestingMode('mock_exams');
  const isTesterExams = localStorage.getItem("role") === "tester";
  const [tab, setTab] = useState(TAB_EXAM);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [starting, setStarting] = useState(null);
  const [categories, setCategories] = useState([]);
  const [filterCategory, setFilterCategory] = useState('');
  const [search, setSearch] = useState('');
  const inProgressToastShownRef = useRef(false);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [resumeDialogExam, setResumeDialogExam] = useState(null);

  // Result tab: one row per exam (averages)
  const [examSummaries, setExamSummaries] = useState([]);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [resultSearch, setResultSearch] = useState('');
  const [resultSearchSubmitted, setResultSearchSubmitted] = useState('');
  const [showPracticeResults, setShowPracticeResults] = useState(false);
  const [attemptsPagination, setAttemptsPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 25,
    total: 0,
  });

  useEffect(() => {
    const handleExamModeChanged = () => {
      window.location.reload();
    };
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  const fetchExamSummaries = useCallback(async (page = 1, searchTerm = '', includeTest = false) => {
    setAttemptsLoading(true);
    try {
      const params = { page, per_page: 25 };
      if (searchTerm) params.search = searchTerm;
      if (includeTest) params.include_test = 1;
      const res = await api.get('/student/mock-exam/results-by-exam', { params });
      const payload = res.data?.data ?? res.data;
      const list = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
      setExamSummaries(list);
      setAttemptsPagination({
        current_page: payload?.current_page ?? 1,
        last_page: payload?.last_page ?? 1,
        per_page: payload?.per_page ?? 25,
        total: payload?.total ?? 0,
      });
    } catch (err) {
      toast.error('Failed to load results');
      setExamSummaries([]);
    } finally {
      setAttemptsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === TAB_RESULT) {
      fetchExamSummaries(attemptsPagination.current_page, resultSearchSubmitted, showPracticeResults);
    }
  }, [tab, resultSearchSubmitted, attemptsPagination.current_page, showPracticeResults, fetchExamSummaries]);

  useEffect(() => {
    // Auto-enable practice results filter while in practice mode.
    if (isTestMode && !showPracticeResults) {
      setShowPracticeResults(true);
      setAttemptsPagination((prev) => ({ ...prev, current_page: 1 }));
    } else if (!isTestMode && showPracticeResults) {
      setShowPracticeResults(false);
      setAttemptsPagination((prev) => ({ ...prev, current_page: 1 }));
    }
  }, [isTestMode, showPracticeResults]);

  useEffect(() => {
    const urlTab = new URLSearchParams(location.search || '').get('tab');
    if (urlTab === 'exam') {
      setTab(TAB_EXAM);
      return;
    }
    if (urlTab === 'result') {
      setTab(TAB_RESULT);
      return;
    }

    if (location.state?.openMockExamResult) {
      setTab(TAB_RESULT);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.search, location.state, location.pathname]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [examsRes, catRes] = await Promise.all([
        api.get('/student/mock-exams'),
        api.get('/student/mock-exam/categories').catch(() => ({ data: { data: [] } })),
      ]);
      const examList = examsRes.data?.data || [];
      setExams([...examList].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      // Root-level categories with child_ids for filtering
      const apiCats = catRes.data?.data || [];
      if (apiCats.length > 0) {
        setCategories(apiCats.map((c) => ({ id: c.id, name: c.name, child_ids: c.child_ids || [] })));
      } else {
        // Fallback: extract unique categories from exam data (no hierarchy available)
        const catMap = {};
        examList.forEach((e) => {
          if (e.category_id && e.category) {
            catMap[e.category_id] = { id: e.category_id, name: e.category, child_ids: [] };
          }
        });
        setCategories(Object.values(catMap));
      }
      // Show toast if any exam is in progress (show once per mount)
      if (!inProgressToastShownRef.current && examList.some((e) => e.has_in_progress)) {
        inProgressToastShownRef.current = true;
        toast.info('You have an exam in progress. Resume it from the card below.', {
          toastId: 'in-progress-exam',
          autoClose: 5000,
        });
      }
    } catch (err) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const focusId = new URLSearchParams(location.search || "").get("focusMockExam");
    if (!focusId || tab !== TAB_EXAM || loading || exams.length === 0) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      const safeId = String(focusId).replace(/[^0-9]/g, "");
      if (!safeId) return;
      const el = document.querySelector(`[data-mock-exam-id="${safeId}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [location.search, tab, loading, exams]);

  const handleStart = async (exam, startFresh = false, isPausedResume = false) => {
    setStarting(exam.id);
    try {
      const res = await api.post(`/student/mock-exam/${exam.id}/start`, startFresh ? { start_fresh: true } : {});
      const sessionData = res.data?.data;
      if (startFresh) {
        sessionStorage.removeItem(`examAnswers_${exam.id}`);
        sessionStorage.removeItem(`examQuestion_${exam.id}`);
        sessionStorage.removeItem(`examScratch_${exam.id}`);
        sessionStorage.removeItem(`examFlagged_${exam.id}`);
        sessionStorage.removeItem(`examPaused_${exam.id}`);
      }
      navigate(`/student/mock-exam/${exam.id}/take`, { state: { session: sessionData, startFresh, isPausedResume, isTestMode: sessionData?.is_test_mode || isTestMode } });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start exam');
    } finally {
      setStarting(null);
    }
  };

  const handleExamButtonClick = (exam) => {
    if (exam.has_in_progress) {
      // Always show popup for any in-progress attempt (paused or not)
      setResumeDialogExam(exam);
      setResumeDialogOpen(true);
    } else {
      // New exam or retake — go directly
      handleStart(exam);
    }
  };

  const handleResumeChoice = async (choice) => {
    if (!resumeDialogExam) return;
    const exam = resumeDialogExam;
    const isPaused = exam.has_in_progress && !!sessionStorage.getItem(`examPaused_${exam.id}`);
    setResumeDialogOpen(false);
    setResumeDialogExam(null);
    if (choice === 'startFresh') {
      await handleStart(exam, true, false);
    } else {
      // Resume — if the exam was paused, load it back in paused state so timer waits
      await handleStart(exam, false, isPaused);
    }
  };

  const autoSelectedCategoryRef = useRef(false);
  useEffect(() => {
    if (categories.length === 0 || autoSelectedCategoryRef.current) return;
    const match = categories.find((c) => c.name.toLowerCase().includes('mock exam'));
    if (match) {
      setFilterCategory(String(match.id));
      autoSelectedCategoryRef.current = true;
    }
  }, [categories]);

  const filteredExams = exams.filter((exam) => {
    let matchesCategory = true;
    if (filterCategory) {
      const cat = categories.find((c) => String(c.id) === String(filterCategory));
      const allowedIds = cat ? [cat.id, ...cat.child_ids] : [filterCategory];
      matchesCategory = allowedIds.some((id) => String(id) === String(exam.category_id));
    }
    const matchesSearch = !search || exam.name?.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleResultSearch = (e) => {
    e?.preventDefault();
    setResultSearchSubmitted(resultSearch);
    setAttemptsPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleResultClear = () => {
    setResultSearch('');
    setResultSearchSubmitted('');
    setAttemptsPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const formatResultDate = (iso) => {
    if (!iso) return 'N/A';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return iso;
    }
  };

  if (loading && tab === TAB_EXAM) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (loadError && tab === TAB_EXAM) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: 2 }}>
        <Alert severity="error" sx={{ maxWidth: 420 }}>
          Unable to load exams. Please check your connection and try again.
        </Alert>
        <Button variant="contained" onClick={fetchData}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3 }}
      >
        <Tab label="Exam" value={TAB_EXAM} icon={<QuizIcon />} iconPosition="start" />
        <Tab label="Result" value={TAB_RESULT} icon={<BarChartIcon />} iconPosition="start" />
      </Tabs>

      {isTestMode && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}>
          PRACTICE MODE — Practice freely. Answers will not be graded.
        </Alert>
      )}

      {tab === TAB_EXAM && (
        <Box>
          {/* Page Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {isTestMode ? 'Practice Exams' : 'Available Exams'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isTestMode ? 'Practice mode is active. Your results will not be recorded.' : 'Choose an exam to start or retake. Good luck!'}
            </Typography>
          </Box>

          {/* Filters row */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search exams..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, minWidth: { sm: 220 }, maxWidth: { xs: '100%', sm: 'none' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {categories.length > 0 && (
              <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, minWidth: { sm: 200 }, maxWidth: { xs: '100%', sm: 'none' } }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filterCategory}
                  label="Category"
                  onChange={(e) => setFilterCategory(e.target.value)}
                  renderValue={(val) => {
                    if (!val) return 'All Categories';
                    const cat = categories.find((c) => String(c.id) === String(val));
                    return cat?.name || 'All Categories';
                  }}
                >
                  <MenuItem value="">
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Radio size="small" checked={!filterCategory} />
                    </ListItemIcon>
                    All Categories
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Radio size="small" checked={String(filterCategory) === String(cat.id)} />
                      </ListItemIcon>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

      {/* In-progress resume banner (examplify-style) */}
      {filteredExams.some((e) => e.has_in_progress) && (
        <Alert severity="info" icon={false} sx={{ mb: 3, borderRadius: 2, bgcolor: '#ede9fe', color: '#5b21b6', border: '1px solid #c4b5fd', '& .MuiAlert-message': { fontWeight: 600 } }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            You have {filteredExams.filter((e) => e.has_in_progress).length === 1
              ? 'an exam'
              : `${filteredExams.filter((e) => e.has_in_progress).length} exams`} in progress.
            Resume from the exam card below to continue where you left off.
          </Typography>
        </Alert>
      )}

      {/* Exam List — flat grid */}
      {filteredExams.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No mock exams are currently available{search || filterCategory ? ' matching your filters' : ' to you'}.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {filteredExams.map((exam) => {
            const isStarting = starting === exam.id;
            const noAttempts = !isTestMode && exam.attempts_left === 0 && !exam.has_in_progress;
            const hasAttempted = exam.has_attempted || (exam.attempts_left !== undefined && exam.attempts_left < (exam.retake_attempts || 2));
            const isPaused = exam.has_in_progress && !!sessionStorage.getItem(`examPaused_${exam.id}`);

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={exam.id}>
                <Card
                  data-mock-exam-id={exam.id}
                  sx={{
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    transition: 'box-shadow 0.2s',
                    border: exam.has_in_progress ? '2px solid #7c3aed' : '1px solid #e0e0e0',
                    '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
                  }}
                >
                  <CardContent sx={{ p: 2, pb: '12px !important', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    {/* Status chips */}
                    {(exam.has_in_progress || isPaused) && (
                      <Box sx={{ mb: 0.75 }}>
                        {exam.has_in_progress && !isPaused && (
                          <Chip label="In Progress" size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22, bgcolor: '#ede9fe', color: '#5b21b6', border: '1px solid #c4b5fd' }} />
                        )}
                        {isPaused && (
                          <Chip icon={<PauseCircle sx={{ fontSize: 12 }} />} label="Paused" size="small" color="info" sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22 }} />
                        )}
                      </Box>
                    )}

                    {/* Exam name */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.75 }}>
                      {exam.name}
                    </Typography>

                    {/* Compact info row */}
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1, alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <QuizIcon sx={{ fontSize: 13 }} /> {exam.questions_count || 0}Q
                      </Typography>
                      {exam.duration_minutes && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <AccessTime sx={{ fontSize: 13 }} /> {exam.duration_minutes}m
                        </Typography>
                      )}
                      {isTestMode ? (
                        <Chip label="Unlimited Practice" size="small" color="info" sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                      ) : exam.attempts_left !== undefined && (
                        <Typography variant="caption" color={noAttempts ? 'error' : 'text.secondary'} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Replay sx={{ fontSize: 13 }} /> {exam.attempts_left} left
                        </Typography>
                      )}
                      {isTesterExams && exam.total_marks > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3, fontWeight: 600 }}>
                          {exam.total_marks}m
                        </Typography>
                      )}
                    </Box>

                    {/* Action button */}
                    <Box sx={{ mt: 'auto' }}>
                      <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        onClick={() => handleExamButtonClick(exam)}
                        disabled={isStarting || noAttempts}
                        startIcon={
                          isStarting ? <CircularProgress size={14} color="inherit" />
                          : isPaused ? <PauseCircle sx={{ fontSize: 16 }} />
                          : exam.has_in_progress ? <PlayArrow sx={{ fontSize: 16 }} />
                          : hasAttempted ? <Replay sx={{ fontSize: 16 }} />
                          : <PlayArrow sx={{ fontSize: 16 }} />
                        }
                        sx={{
                          background: noAttempts ? '#bdbdbd'
                            : isPaused ? 'linear-gradient(90deg, #667eea, #764ba2)'
                            : exam.has_in_progress ? 'linear-gradient(90deg, #667eea, #764ba2)'
                            : 'linear-gradient(90deg, #1a1a1a, #3a3a3a)',
                          textTransform: 'none',
                          fontWeight: 700,
                          py: 0.75,
                          borderRadius: 1.5,
                          fontSize: '0.82rem',
                          '&:hover': { opacity: 0.9 },
                          '&:disabled': { background: '#ccc', color: 'white' },
                        }}
                      >
                        {isStarting ? 'Starting...'
                          : noAttempts ? 'No Attempts'
                          : isPaused ? 'Resume (Paused)'
                          : exam.has_in_progress ? 'Resume'
                          : isTestMode ? 'Start Practice'
                          : hasAttempted ? 'Retake'
                          : 'Start'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
        </Box>
      )}

      {/* Resume Exam Dialog — shown when clicking Resume/Start on exam with in-progress attempt */}
      <Dialog open={resumeDialogOpen} onClose={() => { setResumeDialogOpen(false); setResumeDialogExam(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Resume Exam?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            You have an unfinished attempt for this exam. Resume from where you left off, or start fresh?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => handleResumeChoice('startFresh')}
            sx={{ textTransform: 'none' }}
          >
            Start Fresh
          </Button>
          <Button
            variant="contained"
            onClick={() => handleResumeChoice('resume')}
            sx={{ textTransform: 'none' }}
          >
            Resume
          </Button>
        </DialogActions>
      </Dialog>

      {tab === TAB_RESULT && (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                My Results
              </Typography>
              <Typography variant="body2" color="text.secondary">
                One row per exam with your average score. Open Report to see every attempt and averages.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, alignItems: 'center' }}>
              <Box component="form" onSubmit={handleResultSearch} sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Search results"
                  value={resultSearch}
                  onChange={(e) => setResultSearch(e.target.value)}
                  sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, minWidth: { sm: 220 }, maxWidth: { xs: '100%', sm: 'none' } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button type="submit" variant="contained" size="medium">
                  Search
                </Button>
              </Box>
              {resultSearchSubmitted && (
                <Button variant="outlined" size="small" onClick={handleResultClear}>
                  Clear
                </Button>
              )}
              {isTestMode && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                  <Typography variant="body2" color="text.secondary">Show practice results</Typography>
                  <input
                    type="checkbox"
                    checked={showPracticeResults}
                    onChange={(e) => {
                      setShowPracticeResults(e.target.checked);
                      setAttemptsPagination((prev) => ({ ...prev, current_page: 1 }));
                    }}
                  />
                </Box>
              )}
            </Box>
            {attemptsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : examSummaries.length > 0 ? (
              <>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                  <Table size="medium">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Exam</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Last completed</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Attempts</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Average score</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {examSummaries.map((row) => (
                        <TableRow key={row.mock_exam_id}>
                          <TableCell sx={{ fontWeight: 500 }}>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {row.exam_name}
                                </Typography>
                                {row.has_practice_attempts && (
                                  <Chip
                                    size="small"
                                    label="Includes practice"
                                    color="info"
                                    sx={{ fontWeight: 700, fontSize: '0.65rem', height: 20 }}
                                  />
                                )}
                              </Box>
                              {row.category_name && (
                                <Typography variant="caption" color="text.secondary">
                                  {row.category_name}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{formatResultDate(row.last_completed_at)}</TableCell>
                          <TableCell>{row.attempt_count}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Tooltip title="Mean percentage across attempts (AI-adjusted when available)">
                                <Chip
                                  size="small"
                                  icon={<BarChartIcon sx={{ fontSize: 13 }} />}
                                  label={`Avg ${typeof row.avg_percentage === 'number' ? row.avg_percentage : Number(row.avg_percentage).toFixed(1)}%`}
                                  color={Number(row.avg_percentage) >= 50 ? 'success' : 'error'}
                                  sx={{ fontWeight: 600 }}
                                />
                              </Tooltip>
                              {row.total_marks != null && row.total_marks > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                                  Avg marks: {Number(row.avg_score ?? 0).toFixed(1)} / {Number(row.total_marks).toFixed(0)}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                navigate(`/student/mock-exam/exam/${row.mock_exam_id}/report`, {
                                  state: { includePracticeResults: showPracticeResults },
                                })
                              }
                            >
                              Report
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={attemptsPagination.current_page <= 1}
                    onClick={() =>
                      setAttemptsPagination((prev) => ({ ...prev, current_page: Math.max(1, prev.current_page - 1) }))
                    }
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={attemptsPagination.current_page >= attemptsPagination.last_page}
                    onClick={() =>
                      setAttemptsPagination((prev) => ({
                        ...prev,
                        current_page: Math.min(prev.last_page, prev.current_page + 1),
                      }))
                    }
                  >
                    Next
                  </Button>
                </Box>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  No Results Found
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  Complete an exam to see your results here.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default StudentMockExams;
